import { useAuth0 } from "@auth0/auth0-react";
import { useQuery } from "@tanstack/react-query";
import { userStatusQueryOptions } from "../lib/userQueries";

export type UserType = "b2c" | "b2b" | "partner" | "d2c";

const USER_TYPE_CLAIM = "https://eraborn.com/user_type";

const USER_TYPES: readonly UserType[] = ["b2c", "b2b", "partner", "d2c"];

/** Narrow an arbitrary backend value to a known UserType, else null. */
function toUserType(value: unknown): UserType | null {
	return USER_TYPES.includes(value as UserType) ? (value as UserType) : null;
}

/**
 * Reads the user's `user_type`. `isD2C` is the single gate for all
 * direct-to-consumer UI behavior — branch on it, never re-read the claim ad hoc.
 *
 * Hardening (handoff item 3): the ID-token claim is fragile — a silent token
 * renewal can transiently drop `user_type`, flipping the gate to b2c mid-session.
 * We source the authoritative value from `/api/user/status` and cache it for the
 * whole session (`staleTime: Infinity`, no refetch), so a dropped claim can't
 * flip it. The ID-token claim is only a fallback while that query is in flight,
 * preserving the previous behavior on first paint.
 */
export function useUserType(): {
	userType: UserType | undefined;
	isD2C: boolean;
} {
	const { user, isAuthenticated } = useAuth0();
	const claimType = toUserType(user?.[USER_TYPE_CLAIM]) ?? undefined;

	const { data: serverType } = useQuery({
		// Shared /user/status query (see userQueryOptions) so this dedupes with
		// LanguageProvider et al. staleTime: Infinity is carried by the options —
		// user_type is immutable for the session, so no token renewal can flip it.
		...userStatusQueryOptions(user?.sub),
		enabled: isAuthenticated,
		retry: 1,
		// Only a known literal may override the claim; unexpected backend values
		// fall back to the Auth0 claim rather than driving isD2C.
		select: (status) => toUserType(status.user_type),
	});

	// Prefer the authoritative server value once loaded; fall back to the
	// ID-token claim during initial load. `null` (server has no type) also falls
	// back to the claim rather than forcing undefined.
	//
	// Default to "d2c" when neither source yields a known type: a new self-serve
	// (e.g. Google) signup has no user_type assigned yet, and those users are
	// direct-to-consumer (86ey3e9yr). b2b/partner/b2c are explicit values, and
	// B2B users carry the Auth0 claim from the JWT, so this default only applies
	// to genuinely type-less self-serve accounts.
	const userType = serverType ?? claimType ?? "d2c";
	return { userType, isD2C: userType === "d2c" };
}
